import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, Field, Notice, Status } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession, useViewer } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Second-factor enrolment.
 *
 * Two facts are shown separately and deliberately: whether the *account* has a
 * second factor, and whether *this session* proved one. They differ — enrolling
 * does not retroactively make the current session privileged — and a page that
 * showed only "MFA on" would leave someone confused about why an action is still
 * refused (SDAD Section 12.2).
 */
export function SecurityPage() {
  const viewer = useViewer();
  const { reload, signOut } = useSession();
  const [enrolment, setEnrolment] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function begin(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setEnrolment(
        await api.request<{ secret: string; otpauthUri: string }>('/api/v1/auth/mfa/totp/enrol', {
          method: 'POST',
        }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.request<{ recoveryCodes: string[] }>(
        '/api/v1/auth/mfa/totp/confirm',
        { method: 'POST', body: { code } },
      );
      setRecoveryCodes(result.recoveryCodes);
      setEnrolment(null);
      setCode('');
      await reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gc-main">
      <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '46rem' }}>
        <div className="gc-page-header">
          <span className="gc-eyebrow">Your account</span>
          <h1>Security</h1>
          <p className="gc-lede">
            Managing roles, supervising a gate and anything touching money all need a second factor.
            So does every CABS operations capability.
          </p>
        </div>

        <FailureNotice error={error} />

        <Card title="Authenticator app" eyebrow="Second factor">
          <div className="gc-stack">
            <div className="gc-row">
              <Status
                tone={viewer.mfa.enrolled ? 'positive' : 'caution'}
                label={viewer.mfa.enrolled ? 'Enrolled' : 'Not enrolled'}
              />
              <Status
                tone={viewer.mfa.satisfiedForSession ? 'positive' : 'neutral'}
                label={
                  viewer.mfa.satisfiedForSession
                    ? 'This session is verified'
                    : 'This session is not verified'
                }
                title="Enrolment belongs to your account. Proof belongs to the session you are using."
              />
            </div>

            {viewer.mfa.enrolled && !viewer.mfa.satisfiedForSession && (
              <Notice tone="caution" title="Sign in again to use privileged actions">
                Your account has a second factor, but you did not present a code when this session
                started.
                <div style={{ marginTop: '0.5rem' }}>
                  <Button onClick={() => void signOut()}>Sign out and back in</Button>
                </div>
              </Notice>
            )}

            {!viewer.mfa.enrolled && enrolment === null && recoveryCodes === null && (
              <>
                <p className="gc-muted">
                  Use any standard authenticator app. GateCheckPro uses ordinary TOTP — nothing
                  proprietary, nothing to install from us.
                </p>
                <Button variant="primary" busy={busy} onClick={() => void begin()}>
                  Set up an authenticator app
                </Button>
              </>
            )}

            {enrolment !== null && (
              <form className="gc-stack" onSubmit={(event) => void confirm(event)}>
                <Notice tone="info" title="Add this to your authenticator app">
                  Enter the key by hand, or open the link on the device holding your authenticator.
                </Notice>
                <div className="gc-stack gc-stack--tight">
                  <span className="gc-field__label">Setup key</span>
                  <code className="gc-mono" style={{ wordBreak: 'break-all', fontSize: '0.9rem' }}>
                    {enrolment.secret}
                  </code>
                </div>
                <Field
                  label="Code from your app"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                  hint="Confirming proves the app really has the key — otherwise you would find out only when locked out."
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
                <Button type="submit" variant="primary" busy={busy}>
                  Confirm and turn on
                </Button>
              </form>
            )}

            {recoveryCodes !== null && (
              <Notice tone="caution" title="Save these recovery codes now">
                Each works once, and they are shown only here — they are stored hashed, so nobody,
                including CABS, can recover them for you.
                <ul
                  className="gc-mono"
                  style={{
                    marginTop: '0.75rem',
                    columns: 2,
                    paddingInlineStart: '1.1rem',
                    fontSize: '0.9rem',
                  }}
                >
                  {recoveryCodes.map((recoveryCode) => (
                    <li key={recoveryCode}>{recoveryCode}</li>
                  ))}
                </ul>
                <div style={{ marginTop: '0.75rem' }}>
                  <Button onClick={() => void signOut()}>
                    Sign out and back in to verify this session
                  </Button>
                </div>
              </Notice>
            )}
          </div>
        </Card>

        <Card title="Email verification" eyebrow="Your account">
          <div className="gc-stack gc-stack--tight">
            <div className="gc-row">
              <Status
                tone={viewer.emailVerified ? 'positive' : 'neutral'}
                label={viewer.emailVerified ? 'Verified' : 'Not verified'}
              />
              <span className="gc-muted">{viewer.email}</span>
            </div>
            <p className="gc-muted">
              Verification messages are not sent in this release. Nothing is blocked by an
              unverified address today; that changes when checkout ships.
            </p>
          </div>
        </Card>

        <p className="gc-muted">
          <Link to="/organizations">Back to your organizations</Link>
        </p>
      </div>
    </main>
  );
}
